/**
 * Helpers compartidos para el cálculo de notas/evaluaciones del curso.
 * Usados tanto por el modal manual ("Mis Cursos → Evaluaciones del Curso")
 * como por el sync automático desde el dashboard BIM cuando el docente
 * publica una entrega con nota.
 *
 * Pesos del promedio final (deben sumar 1.0):
 *   modules_average   10%
 *   evaluation_1      20%   ← entrega 1 BIM
 *   evaluation_2      20%   ← entrega 2 BIM
 *   evaluation_3      20%   ← entrega 3 BIM
 *   final_evaluation  30%   ← entrega 4 BIM
 */

export interface EvaluationFields {
  modules_average?:   number | null;
  evaluation_1?:      number | null;
  evaluation_2?:      number | null;
  evaluation_3?:      number | null;
  final_evaluation?:  number | null;
}

/**
 * Calcula el promedio final ponderado SOLO sobre las evaluaciones que ya
 * están ingresadas (no null, > 0). Las vacías NO cuentan como 0 — eso
 * arrastraba el promedio para abajo dando notas tipo 1.2 cuando había una
 * sola nota de 6.0 ingresada.
 *
 * Si solo Eval 1 = 6.0 está ingresada → promedio = 6.0 (no 1.2).
 * Si Eval 1 = 6.0 y Eval 2 = 7.0     → promedio = 6.5.
 * Si todas las 5 están ingresadas    → promedio ponderado normal.
 */
export function calculateFinalAverage(e: EvaluationFields): number {
  const items: { value: number | null | undefined; weight: number }[] = [
    { value: e.modules_average,  weight: 0.1 },
    { value: e.evaluation_1,     weight: 0.2 },
    { value: e.evaluation_2,     weight: 0.2 },
    { value: e.evaluation_3,     weight: 0.2 },
    { value: e.final_evaluation, weight: 0.3 },
  ];
  const valid = items.filter(it => it.value != null && it.value > 0);
  if (valid.length === 0) return 0;
  const sumW = valid.reduce((a, it) => a + (it.value as number) * it.weight, 0);
  const sumP = valid.reduce((a, it) => a + it.weight, 0);
  return parseFloat((sumW / sumP).toFixed(1));
}

/**
 * Mapea entrega_num del BIM al campo correspondiente en course_evaluations.
 *   Entrega 1 → evaluation_1
 *   Entrega 2 → evaluation_2
 *   Entrega 3 → evaluation_3
 *   Entrega 4 → final_evaluation
 */
export function entregaNumToEvalField(num: number): keyof EvaluationFields | null {
  switch (num) {
    case 1: return 'evaluation_1';
    case 2: return 'evaluation_2';
    case 3: return 'evaluation_3';
    case 4: return 'final_evaluation';
    default: return null;
  }
}
